import {
  ArrowRight,
  CalendarCheck,
  Flag,
  LogOut,
  ShoppingBag,
  TrendingDown,
  Wallet,
} from "lucide-react";
import { motion } from "motion/react";
import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { CardLimitAlerts } from "@/components/domain/CardLimitAlerts";
import { ChartSummary } from "@/components/domain/ChartSummary";
import { EventRow } from "@/components/domain/EventRow";
import {
  IndividualAccountsToggle,
  JointOnlyBadge,
} from "@/components/domain/IndividualAccountsToggle";
import {
  Card,
  EmptyState,
  ErrorState,
  GlassCard,
  IconButton,
  Money,
  Page,
  PageSkeleton,
  ProgressRing,
  Section,
  Skeleton,
} from "@/components/ui";
import { formatMoney } from "@/lib/format";
import { useMembers } from "@/lib/members";
import { ratio, toCents } from "@/lib/money";
import { fadeUp, press } from "@/lib/motion";
import { useDashboard, useDebtChart, useLogout, useMe } from "@/lib/queries";

// Recharts is ~100 kB gzip and only this chart needs it on Inicio: keep it out of the entry chunk (#67)
const DebtArea = lazy(() =>
  import("@/components/domain/Charts").then((m) => ({ default: m.DebtArea })),
);

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Buenos días";
  if (h < 19) return "Buenas tardes";
  return "Buenas noches";
}

export function DashboardPage() {
  const me = useMe();
  const dash = useDashboard();
  const debt = useDebtChart();
  const logout = useLogout();
  const { single } = useMembers();

  const d = dash.data;
  const income = d
    ? toCents(d.income_actual) > toCents(d.income_expected)
      ? d.income_actual
      : d.income_expected
    : "0";
  const spentRatio = d ? ratio(d.spent, d.expected_total) : 0;

  return (
    <Page
      wide
      title={me.data ? `Hola, ${me.data.display_name}` : "Inicio"}
      subtitle={
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {d ? `${greeting()} · ${d.period.label}` : greeting()}
          <JointOnlyBadge />
        </span>
      }
      actions={
        <>
          <IndividualAccountsToggle />
          <IconButton label="Cerrar sesión" onClick={() => logout.mutate()}>
            <LogOut className="size-5" />
          </IconButton>
        </>
      }
    >
      {dash.isLoading ? (
        <PageSkeleton />
      ) : dash.isError || !d ? (
        <ErrorState error={dash.error} onRetry={() => dash.refetch()} />
      ) : (
        <div className="grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
          <div className="space-y-6">
            <GlassCard>
              <div className="text-subhead font-medium text-muted">Patrimonio neto</div>
              <Money value={d.net_worth} animated className="mt-1 block text-hero" />
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-footnote text-muted">
                <span>
                  Disponible en débito y efectivo{" "}
                  <Money value={d.cash_total} className="font-semibold text-text" />
                </span>
                <span>
                  − Crédito del periodo{" "}
                  <Money value={d.credit_period_total} className="font-semibold text-text" />
                </span>
              </div>
            </GlassCard>

            <div className="grid grid-cols-2 gap-3">
              <Card className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-footnote font-medium text-muted">Gastado</span>
                  <ProgressRing ratio={spentRatio} size={44} stroke={5} />
                </div>
                <div>
                  <Money value={d.spent} className="block text-title" />
                  <span className="text-footnote text-muted">
                    de {formatMoney(d.expected_total, { compact: true })} esperado
                  </span>
                </div>
              </Card>
              <Link to="/deudas" className="block">
                <Card className="h-full space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-footnote font-medium text-muted">Deuda</span>
                    <TrendingDown className="size-5 text-muted" aria-hidden />
                  </div>
                  <div>
                    <Money value={d.debt_total} className="block text-title" />
                    <span className="text-footnote text-muted">
                      MSI {formatMoney(d.deferred_total, { compact: true })} · préstamos{" "}
                      {formatMoney(d.debts_total, { compact: true })}
                    </span>
                  </div>
                </Card>
              </Link>
              <Card className="space-y-1">
                <div className="flex items-center gap-1.5 text-footnote font-medium text-muted">
                  <Wallet className="size-4" aria-hidden /> Ingresos
                </div>
                <Money value={income} className="block text-title text-positive" />
                <span className="text-footnote text-muted">
                  {toCents(d.income_actual) > 0
                    ? "recibidos este periodo"
                    : "esperados este periodo"}
                </span>
              </Card>
              <Link to="/puedo-comprar" className="block">
                <motion.div
                  variants={fadeUp}
                  whileTap={press}
                  className="flex h-full flex-col justify-between rounded-lg bg-accent p-4 text-accent-contrast"
                >
                  <ShoppingBag className="size-6" aria-hidden />
                  <div>
                    <div className="text-headline">¿Puedo comprarlo?</div>
                    <div className="flex items-center gap-1 text-footnote opacity-85">
                      Revisa tu margen <ArrowRight className="size-3.5" />
                    </div>
                  </div>
                </motion.div>
              </Link>
            </div>
          </div>

          <div className="space-y-6">
            {d.closed_period && (
              <Link to={`/cierre/${d.closed_period.key}`} className="block">
                <motion.div variants={fadeUp} whileTap={press}>
                  <Card className="flex items-center gap-3">
                    <span className="grid size-10 shrink-0 place-items-center rounded-[10px] bg-accent-soft text-accent">
                      <Flag className="size-5" aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-headline">Cierre del periodo</div>
                      <div className="truncate text-footnote text-muted">
                        {d.closed_period.label}: cómo {single ? "te" : "les"} fue
                      </div>
                    </div>
                    <ArrowRight className="size-4 shrink-0 text-muted" aria-hidden />
                  </Card>
                </motion.div>
              </Link>
            )}
            <CardLimitAlerts />
            <Section
              title="Próximos 21 días"
              action={
                <Link to="/calendario" className="text-subhead font-medium text-accent">
                  Calendario
                </Link>
              }
            >
              {d.upcoming.length === 0 ? (
                <EmptyState
                  icon={CalendarCheck}
                  title="Todo al día"
                  message="No hay pagos próximos."
                />
              ) : (
                d.upcoming.map((e, i) => <EventRow key={`${e.date}-${e.title}-${i}`} event={e} />)
              )}
            </Section>
          </div>

          <Card className="space-y-3 lg:col-span-2">
            <div className="flex items-baseline justify-between">
              <h2 className="text-headline">Deuda restante</h2>
              <Link to="/graficos" className="text-subhead font-medium text-accent">
                Ver gráficos
              </Link>
            </div>
            {debt.data && debt.data.length > 0 ? (
              <>
                <ChartSummary>
                  Hoy debes {formatMoney(d.debt_total)}; en 6 periodos quedarían{" "}
                  {formatMoney(debt.data[Math.min(6, debt.data.length - 1)]!.total)}.
                </ChartSummary>
                <Suspense fallback={<Skeleton className="h-[254px]" />}>
                  <DebtArea data={debt.data.slice(0, 12)} />
                </Suspense>
              </>
            ) : (
              <div className="h-56" />
            )}
          </Card>
        </div>
      )}
    </Page>
  );
}
